import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { updateUserAPI } from '../services/serverService';

const EmployeeVehicleModule = ({ employee, serverId, canEdit }) => {
    // DVLA fields are sometimes spread on the employee object or inside a dvla object
    // For safety, we check both employee level and employee.dvla (if it exists)
    const getField = (fieldName) => {
        return (employee && employee[fieldName]) || (employee?.dvla && employee.dvla[fieldName]) || 'Not specified';
    };

    const vehicleInfo = {
        vrn: getField('vrn'),
        category: getField('dvlaVehicleCategory'),
        make: getField('dvlaVehicleMake'),
        model: getField('dvlaVehicleModel'),
        color: getField('dvlaVehicleColor'),
        fuelType: getField('dvlaVehicleFuelType'),
        firstRegistered: getField('dvlaVehicleRegistered'),
    };

    const drivingPreferences = {
        ownPolicy: getField('drivesWorkVehicleOwnPolicy') === true,
        workplacePolicy: getField('drivesWorkVehicleWorkplacePolicy') === true,
        serviceUserVehicles: getField('drivesServiceUserVehicles') === true,
    };

    const driversLicenseData = employee?.driversLicense || {};
    const carInsuranceData = employee?.carInsurance || {};

    const [dlFile, setDlFile] = useState(null);
    const [dlExpiry, setDlExpiry] = useState(driversLicenseData.expiryDate || '');
    
    const [ciFile, setCiFile] = useState(null);
    const [ciExpiry, setCiExpiry] = useState(carInsuranceData.expiryDate || '');

    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const handleUpload = async (type) => {
        if (!canEdit) return;
        setUploading(true);
        setError('');
        setSuccessMsg('');

        try {
            let fileUrl = null;
            let fileToUpload = type === 'dl' ? dlFile : ciFile;
            let expiryToSave = type === 'dl' ? dlExpiry : ciExpiry;
            const existingUrl = type === 'dl' ? driversLicenseData.file : carInsuranceData.file;

            if (fileToUpload) {
                const fileRef = ref(storage, `servers/${serverId}/employees/${employee.username}/${type}_${Date.now()}_${fileToUpload.name}`);
                await uploadBytes(fileRef, fileToUpload);
                fileUrl = await getDownloadURL(fileRef);
            } else {
                fileUrl = existingUrl;
            }

            const updatePayload = {
                serverId: serverId,
                role: 'employee',
            };

            if (type === 'dl') {
                updatePayload['driversLicense.file'] = fileUrl;
                updatePayload['driversLicense.expiryDate'] = expiryToSave;
            } else {
                updatePayload['carInsurance.file'] = fileUrl;
                updatePayload['carInsurance.expiryDate'] = expiryToSave;
            }

            await updateUserAPI(employee.id, updatePayload);
            setSuccessMsg(`${type === 'dl' ? "Driver's License" : "Car Insurance"} updated successfully! Please refresh to see changes.`);
            
            if (type === 'dl') setDlFile(null);
            if (type === 'ci') setCiFile(null);

        } catch (err) {
            console.error("Error updating document:", err);
            setError("Failed to update document. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const renderBoolean = (val) => (
        <span style={{ ...styles.badge, backgroundColor: val ? '#10b981' : '#64748b' }}>
            {val ? 'Yes' : 'No'}
        </span>
    );

    return (
        <div style={styles.container}>
            <div style={styles.contentSection}>
                {/* Vehicle Details Card */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Vehicle Details</h3>
                    <div style={styles.grid}>
                        <div style={styles.field}>
                            <span style={styles.label}>Vehicle Registration (VRN)</span>
                            <span style={styles.value}>{vehicleInfo.vrn}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Vehicle Category</span>
                            <span style={styles.value}>{vehicleInfo.category}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Make</span>
                            <span style={styles.value}>{vehicleInfo.make}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Model</span>
                            <span style={styles.value}>{vehicleInfo.model}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Color</span>
                            <span style={styles.value}>{vehicleInfo.color}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Fuel Type</span>
                            <span style={styles.value}>{vehicleInfo.fuelType}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>First Registered</span>
                            <span style={styles.value}>{vehicleInfo.firstRegistered}</span>
                        </div>
                    </div>
                </div>

                {/* Driving Preferences Card */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Driving Preferences & Insurance</h3>
                    <div style={styles.list}>
                        <div style={styles.listRow}>
                            <span style={styles.listLabel}>Drives work vehicle on own policy</span>
                            {renderBoolean(drivingPreferences.ownPolicy)}
                        </div>
                        <div style={styles.listRow}>
                            <span style={styles.listLabel}>Drives work vehicle on workplace policy</span>
                            {renderBoolean(drivingPreferences.workplacePolicy)}
                        </div>
                        <div style={styles.listRow}>
                            <span style={styles.listLabel}>Drives service user vehicles</span>
                            {renderBoolean(drivingPreferences.serviceUserVehicles)}
                        </div>
                    </div>
                </div>

                {/* Compliance Documents */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Compliance Documents</h3>
                    
                    {error && <div style={{ color: '#dc2626', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
                    {successMsg && <div style={{ color: '#16a34a', marginBottom: '16px', fontSize: '14px' }}>{successMsg}</div>}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        {/* Driver's License */}
                        <div style={{ border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', backgroundColor: 'white' }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e293b' }}>Driver's License</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div>
                                    <label style={styles.label}>Expiry Date</label>
                                    <input 
                                        type="date" 
                                        value={dlExpiry}
                                        onChange={(e) => setDlExpiry(e.target.value)}
                                        disabled={!canEdit || uploading}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Document File</label>
                                    {driversLicenseData.file && (
                                        <div style={{ marginBottom: '8px' }}>
                                            <a href={driversLicenseData.file} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '14px' }}>View Current License</a>
                                        </div>
                                    )}
                                    {canEdit && (
                                        <input 
                                            type="file" 
                                            onChange={(e) => setDlFile(e.target.files[0])}
                                            disabled={uploading}
                                            style={{ width: '100%', fontSize: '14px' }}
                                        />
                                    )}
                                </div>
                                {canEdit && (
                                    <button 
                                        onClick={() => handleUpload('dl')}
                                        disabled={uploading || (!dlFile && dlExpiry === driversLicenseData.expiryDate)}
                                        style={{ ...styles.saveButton, opacity: (uploading || (!dlFile && dlExpiry === driversLicenseData.expiryDate)) ? 0.5 : 1 }}
                                    >
                                        {uploading ? 'Saving...' : 'Save Driver\'s License'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Car Insurance */}
                        <div style={{ border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', backgroundColor: 'white' }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e293b' }}>Car Insurance</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div>
                                    <label style={styles.label}>Expiry Date</label>
                                    <input 
                                        type="date" 
                                        value={ciExpiry}
                                        onChange={(e) => setCiExpiry(e.target.value)}
                                        disabled={!canEdit || uploading}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Document File</label>
                                    {carInsuranceData.file && (
                                        <div style={{ marginBottom: '8px' }}>
                                            <a href={carInsuranceData.file} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '14px' }}>View Current Insurance</a>
                                        </div>
                                    )}
                                    {canEdit && (
                                        <input 
                                            type="file" 
                                            onChange={(e) => setCiFile(e.target.files[0])}
                                            disabled={uploading}
                                            style={{ width: '100%', fontSize: '14px' }}
                                        />
                                    )}
                                </div>
                                {canEdit && (
                                    <button 
                                        onClick={() => handleUpload('ci')}
                                        disabled={uploading || (!ciFile && ciExpiry === carInsuranceData.expiryDate)}
                                        style={{ ...styles.saveButton, opacity: (uploading || (!ciFile && ciExpiry === carInsuranceData.expiryDate)) ? 0.5 : 1 }}
                                    >
                                        {uploading ? 'Saving...' : 'Save Car Insurance'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #f3f4f6',
        padding: '24px',
        marginTop: '20px'
    },
    contentSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    card: {
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        padding: '24px',
    },
    cardTitle: {
        margin: '0 0 20px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        borderBottom: '2px solid #e2e8f0',
        paddingBottom: '10px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    value: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    listRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid #f1f5f9',
    },
    listLabel: {
        fontSize: '15px',
        color: '#334155',
        fontWeight: '500'
    },
    badge: {
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#ffffff',
        letterSpacing: '0.5px'
    },
    saveButton: {
        marginTop: '8px',
        padding: '8px 16px',
        backgroundColor: '#2563eb',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    }
};

export default EmployeeVehicleModule;
